import { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { useGetCustomSurveyTemplateTrendsQuery } from '../../hooks/queries/useGetCustomSurveyTemplateTrendsQuery';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import type { PendingSurveyInfo } from '../../api/customSurveys';
import { CategoryResults } from './components/CategoryResults';
import { TemplateHeader } from './components/TemplateHeader';
import { ResultsGuide } from './components/ResultsGuide';
import { formatDate, groupResults, latestPair, validSurveyCount } from './templateTrendsView';
import classes from './CustomSurveyTemplateTrendsPage.module.css';

const PendingSurveyAlert = ({ pendingSurvey }: { pendingSurvey: PendingSurveyInfo }) => {
  const { t } = useTranslation();
  const percentage = pendingSurvey.requiredResponseCount
    ? Math.round((pendingSurvey.currentResponseCount / pendingSurvey.requiredResponseCount) * 100)
    : 0;

  return (
    <Alert data-color="warning" className={classes.notice}>
      <Heading level={3} data-size="2xs">
        {t('customSurveys.templateTrends.pending.title', { surveyTitle: pendingSurvey.surveyTitle })}
      </Heading>
      <Paragraph data-size="sm">
        {t('customSurveys.templateTrends.pending.description', {
          current: pendingSurvey.currentResponseCount,
          required: pendingSurvey.requiredResponseCount,
          percentage,
          rate: pendingSurvey.teamMemberCount <= 5 ? 100 : 75,
        })}
      </Paragraph>
    </Alert>
  );
};

export const CustomSurveyTemplateTrendsPage = () => {
  const { t, i18n } = useTranslation();
  const { templateId } = useParams<{ templateId: string }>();
  const { selectedTeamId } = useSelectedUnitContext();

  const { data: trends, isLoading, error } = useGetCustomSurveyTemplateTrendsQuery(Number(templateId), selectedTeamId);

  const latestSurvey = trends?.questionTrends
    .map((question) => latestPair(question)?.current)
    .filter((point) => point !== undefined)
    .sort((a, b) => new Date(b.surveyActivatedAt).getTime() - new Date(a.surveyActivatedAt).getTime())[0];
  const surveyCount = trends ? validSurveyCount(trends.questionTrends) : 0;

  const page = (content: ReactNode) => (
    <div className={classes.page}>
      <TemplateHeader templateId={templateId ?? ''} title={trends?.templateTitle} surveyId={latestSurvey?.surveyId} />
      {content}
    </div>
  );

  if (isLoading) {
    return page(
      <div aria-busy="true" className={classes.loading}>
        <span className="ds-sr-only">{t('customSurveys.templateTrends.loading')}</span>
        <Skeleton variant="rectangle" width="100%" height={96} />
        <Skeleton variant="rectangle" width="100%" height={360} />
      </div>,
    );
  }

  if (error || !trends) {
    const key = error ? 'loadingError' : 'notFound';
    return page(
      <Alert data-color={error ? 'danger' : 'warning'}>
        <Heading level={3} data-size="2xs">
          {t(`customSurveys.templateTrends.errors.${key}Title`)}
        </Heading>
        <Paragraph>{t(`customSurveys.templateTrends.errors.${key}Message`)}</Paragraph>
      </Alert>,
    );
  }

  if (surveyCount === 0) {
    return page(
      <>
        {trends.pendingSurvey && <PendingSurveyAlert pendingSurvey={trends.pendingSurvey} />}
        {!trends.pendingSurvey && (
          <Alert data-color="info" className={classes.notice}>
            <Heading level={3} data-size="2xs">
              {t('customSurveys.templateTrends.noData.title')}
            </Heading>
            <Paragraph data-size="sm">{t('customSurveys.templateTrends.noData.description')}</Paragraph>
            <Paragraph data-size="sm">{t('customSurveys.templateTrends.noData.requirements')}</Paragraph>
          </Alert>
        )}
      </>,
    );
  }

  const hasComparison = surveyCount > 1;

  return page(
    <>
      <Alert data-color="accent" className={classes.notice}>
        <Heading level={3} data-size="2xs">
          {t('customSurveys.templateTrends.intro.title')}
        </Heading>
        <Paragraph data-size="sm">
          {t(`customSurveys.templateTrends.intro.${hasComparison ? 'comparison' : 'single'}`)}
        </Paragraph>
      </Alert>

      {trends.pendingSurvey && <PendingSurveyAlert pendingSurvey={trends.pendingSurvey} />}

      <dl className={classes.stats}>
        <div>
          <dt>{t('customSurveys.templateTrends.stats.surveys')}</dt>
          <dd>{trends.totalSurveysCreated}</dd>
        </div>
        <div>
          <dt>{t('customSurveys.templateTrends.stats.questions')}</dt>
          <dd>{trends.questionTrends.length}</dd>
        </div>
        {latestSurvey && (
          <div>
            <dt>{t('customSurveys.templateTrends.stats.latest')}</dt>
            <dd>{formatDate(latestSurvey.surveyActivatedAt, i18n.language)}</dd>
          </div>
        )}
      </dl>

      <section aria-labelledby="template-results-heading" className={classes.results}>
        <div className={classes.resultsIntro}>
          <Heading level={2} data-size="sm" id="template-results-heading">
            {t('customSurveys.templateTrends.results.title')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t(`customSurveys.templateTrends.results.${hasComparison ? 'comparison' : 'single'}`)}
          </Paragraph>
          <ResultsGuide view="trends" />
        </div>
        {groupResults(trends.questionTrends).map((group) => (
          <CategoryResults key={group.category ?? 'other'} group={group} />
        ))}
      </section>
    </>,
  );
};
